"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Services", href: "/services" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "Admin", href: "/admin" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-deep-dark/90 backdrop-blur-xl shadow-glass border-b border-divider"
            : "bg-transparent"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-18 flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2 group" aria-label="Helium Fulfilment Home">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <AppLogo
                text="Helium"
                iconName="CubeIcon"
                size={64}
              />
            </motion.div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {navLinks?.map((link, index) => {
              const isActive = pathname === link?.href;
              return (
                <motion.div
                  key={link?.href}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Link
                    href={link?.href}
                    className={`px-4 py-2 rounded-xl text-sm font-medium font-heading transition-all duration-200 ${
                      isActive
                        ? "text-accent-primary bg-accent-primary/10 border border-accent-primary/20" :"text-text-secondary hover:text-accent-primary hover:bg-glass-white"
                    }`}
                  >
                    {link?.label}
                  </Link>
                </motion.div>
              );
            })}
          </nav>

          {/* CTA */}
          <motion.div 
            className="hidden md:flex items-center gap-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Link
              href="/login"
              className="text-sm font-semibold text-text-secondary hover:text-accent-primary transition-colors px-3 py-2"
            >
              Sign In
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent-primary text-white font-semibold text-sm transition-smooth hover:scale-105"
              style={{
                boxShadow: "0 0 30px rgba(91, 46, 255, 0.3)"
              }}
            >
              Book a Call
              <Icon name="ArrowRightIcon" size={14} />
            </Link>
          </motion.div>

          {/* Mobile Menu Button - Enhanced touch target */}
          <button
            className="md:hidden p-3 rounded-lg text-text-secondary hover:bg-deep-purple/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            <Icon name={mobileOpen ? "XMarkIcon" : "Bars3Icon"} size={24} />
          </button>
        </div>
      </motion.header>
      {/* Mobile Menu - Enhanced for touch */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-40 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              className="absolute top-0 right-0 h-full w-full sm:w-80 bg-secondary-dark shadow-2xl"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <div className="p-6 pt-20 space-y-3">
                {navLinks?.map((link, index) => {
                  const isActive = pathname === link?.href;
                  return (
                    <motion.div
                      key={link?.href}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Link
                        href={link?.href}
                        className={`flex items-center gap-3 px-5 py-4 rounded-xl text-base font-semibold font-heading transition-all min-h-[56px] ${
                          isActive
                            ? "text-accent-primary bg-accent-primary/20 border border-accent-primary/30" :"text-text-secondary hover:text-accent-primary hover:bg-glass-white"
                        }`}
                      >
                        {link?.label}
                      </Link>
                    </motion.div>
                  );
                })}
                <motion.div 
                  className="pt-6 border-t border-divider space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                >
                  <Link
                    href="/contact"
                    className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-accent-primary text-white rounded-xl text-base font-bold font-heading hover:bg-accent-secondary transition-all duration-300 hover:scale-[1.02] active:scale-95 min-h-[56px]"
                    style={{
                      boxShadow: "0 0 30px rgba(91, 46, 255, 0.4)"
                    }}
                  >
                    Book a Call
                    <Icon name="ArrowRightIcon" size={18} />
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}